import { AssemblyModalities } from 'helpers/enums/Assembleia';
import moment from 'moment';
import { isDateLessThanToday } from 'helpers/utils/DateUtils';
import { formataStringData } from 'helpers/utils/HTMLUtils';
import {
  DEFAULT_VALID,
  DEFAULT_VALIDATION_MODEL,
  ValidationModel,
  allIsFalse,
  firstDateIsBefore,
  requiredIsValid,
  validaData,
  validaHora,
} from 'helpers/utils/ValidationUtils';
import { ISOToLocaleDateAndHour } from './CloneEvent';

export type CloneEventValidationType = {
  digitalInitialDateIsValid: () => ValidationModel;
  digitalEndDateIsValid: () => ValidationModel;
  digitalInitialHourIsValid: () => ValidationModel;
  digitalEndHourIsValid: () => ValidationModel;
  presentialInitialDateIsValid: () => ValidationModel;
  presentialInitialHourIsValid: () => ValidationModel;
  presentialEndDateIsValid: () => ValidationModel;
  presentialEndHourIsValid: () => ValidationModel;
  quantityAssociatesIsValid: () => ValidationModel;
  allIsValid: () => boolean;
};

export const CloneEventValidation = (
  digitalInitialDate: string,
  digitalEndDate: string,
  digitalInitialHour: string,
  digitalEndHour: string,
  presentialInitialDate?: string,
  presentialEndDate?: string,
  presentialInitialHour?: string,
  presentialEndHour?: string,
  quantityAssociates?: number,
  modality?: AssemblyModalities
) => {
  const validator: Partial<CloneEventValidationType> = {};

  // Date

  validator.digitalInitialDateIsValid = () => {
    const dataInicio = digitalInitialDate;
    const dataFim = digitalEndDate;
    const isValid = validaData(dataInicio!);
    const required = requiredIsValid(
      dataInicio,
      'Data de início é obrigatória'
    );

    if (
      ![AssemblyModalities.OFFLINE].includes(modality!) &&
      new Date(
        new Date(moment(dataInicio, 'DD/MM/YYYY').toDate()).setUTCHours(
          23,
          59,
          59,
          999
        )
      ) < new Date(new Date().setUTCHours(23, 59, 59, 999))
    ) {
      return DEFAULT_VALIDATION_MODEL(
        true,
        'Data início não pode ser inferior a data atual'
      );
    }

    if (required.error) {
      return required;
    }

    if (
      Date.parse(formataStringData(dataInicio).replace(/-/g, '/')) <
      Date.parse(formataStringData('01/01/2018').replace(/-/g, '/'))
    ) {
      return DEFAULT_VALIDATION_MODEL(
        true,
        'Data início não pode ser inferior a 2018'
      );
    }

    if (!isValid) {
      return DEFAULT_VALIDATION_MODEL(true, 'Data inválida');
    }

    if (firstDateIsBefore(dataFim!, dataInicio!)) {
      return DEFAULT_VALIDATION_MODEL(
        true,
        'Data de início deve ser anterior à data de término'
      );
    }

    return DEFAULT_VALID;
  };

  validator.digitalEndDateIsValid = () => {
    const dataFim = digitalEndDate;
    const isValid = validaData(dataFim!);
    const required = requiredIsValid(dataFim, 'Data de término é obrigatória');

    if (required.error) {
      return required;
    }

    if (
      Date.parse(formataStringData(dataFim).replace(/-/g, '/')) <
      Date.parse(formataStringData('01/01/2018').replace(/-/g, '/'))
    ) {
      return DEFAULT_VALIDATION_MODEL(
        true,
        'Data de término não pode ser inferior a 2018'
      );
    }

    if (!isValid) {
      return DEFAULT_VALIDATION_MODEL(true, 'Data inválida');
    }

    const dataInicio = digitalInitialDate;
    if (firstDateIsBefore(dataFim!, dataInicio!)) {
      return DEFAULT_VALIDATION_MODEL(
        true,
        'Data de término deve ser posterior à data de início'
      );
    }
    return DEFAULT_VALID;
  };

  validator.presentialInitialDateIsValid = () => {
    const dataInicio = presentialInitialDate;
    const dataFim = presentialEndDate;
    const dataFimOnline = digitalEndDate;
    const modo = modality;
    const required = requiredIsValid(
      dataInicio,
      'Data de início é obrigatória'
    );

    if ([AssemblyModalities.HIBRIDO_PRESENCIAL_DIGITAL].includes(modo!)) {
      if (
        new Date(
          new Date(moment(dataInicio, 'DD/MM/YYYY').toDate()).setUTCHours(
            23,
            59,
            59,
            999
          )
        ) < new Date(new Date().setUTCHours(23, 59, 59, 999))
      ) {
        return DEFAULT_VALIDATION_MODEL(
          true,
          'Data início não pode ser inferior a data atual'
        );
      }

      if (required.error) {
        return required;
      }

      if (
        Date.parse(formataStringData(String(dataInicio)).replace(/-/g, '/')) <
        Date.parse(formataStringData('01/01/2018').replace(/-/g, '/'))
      ) {
        return DEFAULT_VALIDATION_MODEL(
          true,
          'Data início não pode ser inferior a 2018'
        );
      }

      const isValid = validaData(String(dataInicio!));
      if (!isValid) {
        return DEFAULT_VALIDATION_MODEL(true, 'Data inválida');
      }
      if (firstDateIsBefore(String(dataInicio!), String(dataFimOnline!))) {
        return DEFAULT_VALIDATION_MODEL(
          true,
          'Data de início deve ser posterior à data de término presencial'
        );
      }
      if (firstDateIsBefore(String(dataFim!), String(dataInicio!))) {
        return DEFAULT_VALIDATION_MODEL(
          true,
          'Data de início deve ser anterior à data de término'
        );
      }
    }

    return DEFAULT_VALID;
  };

  validator.presentialEndDateIsValid = () => {
    const dataFim = presentialEndDate;
    const required = requiredIsValid(dataFim, 'Data de término é obrigatória');
    const isValid = validaData(String(dataFim!));
    const modo = modality;

    if ([AssemblyModalities.HIBRIDO_PRESENCIAL_DIGITAL].includes(modo!)) {
      if (required.error) {
        return required;
      }
      if (
        Date.parse(formataStringData(String(dataFim)).replace(/-/g, '/')) <
        Date.parse(formataStringData('01/01/2018').replace(/-/g, '/'))
      ) {
        return DEFAULT_VALIDATION_MODEL(
          true,
          'Data de término não pode ser inferior a 2018'
        );
      }
      if (!isValid) {
        return DEFAULT_VALIDATION_MODEL(true, 'Data inválida');
      }
      const dataInicio = presentialInitialDate;

      if (firstDateIsBefore(String(dataFim!), String(dataInicio!))) {
        return DEFAULT_VALIDATION_MODEL(
          true,
          'Data de término deve ser posterior à data de início'
        );
      }
    }
    return DEFAULT_VALID;
  };

  // Hours

  validator.digitalInitialHourIsValid = () => {
    const initialDate = digitalInitialDate;
    const endDate = digitalEndDate;
    const initialHour = digitalInitialHour;
    const endHour = digitalEndHour;

    const required = requiredIsValid(
      initialHour,
      'Hora de início é obrigatória'
    );
    if (required.error) {
      return required;
    }
    const isValid = validaHora(initialHour);
    if (!isValid) {
      return DEFAULT_VALIDATION_MODEL(true, 'Hora inválida');
    }

    const inicio = moment(`${initialDate} ${initialHour}`, 'DD/MM/YYYY HH:mm');
    const fim = moment(`${endDate} ${endHour}`, 'DD/MM/YYYY HH:mm');
    const fimCurrent = moment(moment(), 'DD/MM/YYYY HH:mm');

    if (![AssemblyModalities.OFFLINE].includes(modality!)) {
      if (inicio.isSameOrBefore(fimCurrent)) {
        return DEFAULT_VALIDATION_MODEL(
          true,
          'Deve ser posterior ao horário atual'
        );
      }
    }

    if (
      initialDate &&
      validaData(initialDate) &&
      endHour &&
      validaHora(endHour) &&
      endDate &&
      validaData(endDate)
    ) {
      if (
        moment(initialDate, 'DD/MM/YYYY').isSame(moment(endDate, 'DD/MM/YYYY'))
      ) {
        if (inicio.isAfter(fim)) {
          return DEFAULT_VALIDATION_MODEL(
            true,
            'Deve ser anterior à hora de término'
          );
        }
      }
    }
    return DEFAULT_VALID;
  };

  validator.digitalEndHourIsValid = () => {
    const initialDate = digitalInitialDate;
    const endDate = digitalEndDate;
    const initialHour = digitalInitialHour;
    const endHour = digitalEndHour;

    const required = requiredIsValid(endHour, 'Hora de término é obrigatória');
    if (required.error) {
      return required;
    }
    const isValid = validaHora(endHour);
    if (!isValid) {
      return DEFAULT_VALIDATION_MODEL(true, 'Hora inválida');
    }
    if (
      initialDate &&
      validaData(initialDate) &&
      initialHour &&
      validaHora(initialHour) &&
      endDate &&
      validaData(endDate)
    ) {
      if (
        moment(initialDate, 'DD/MM/YYYY').isSame(moment(endDate, 'DD/MM/YYYY'))
      ) {
        const inicio = moment(
          `${initialDate} ${initialHour}`,
          'DD/MM/YYYY HH:mm'
        );
        const fim = moment(`${endDate} ${endHour}`, 'DD/MM/YYYY HH:mm');
        if (inicio.isAfter(fim)) {
          return DEFAULT_VALIDATION_MODEL(
            true,
            'Deve ser posterior à hora de início'
          );
        }
      }
    }
    return DEFAULT_VALID;
  };

  validator.presentialInitialHourIsValid = () => {
    const initialDate = presentialInitialDate;
    const endDate = presentialEndDate;
    const endDateOnline = digitalEndDate;
    const initialHour = presentialInitialHour;
    const endHour = presentialEndHour;
    const endHourOnline = digitalEndHour;
    const modo = modality;
    const required = requiredIsValid(
      initialHour,
      'Hora de início é obrigatória'
    );

    if ([AssemblyModalities.HIBRIDO_PRESENCIAL_DIGITAL].includes(modo!)) {
      if (required.error) {
        return required;
      }
      const isValid = validaHora(String(initialHour));
      if (!isValid) {
        return DEFAULT_VALIDATION_MODEL(true, 'Hora inválida');
      }

      const inicio = moment(
        `${initialDate} ${initialHour}`,
        'DD/MM/YYYY HH:mm'
      );

      const fimOnline = moment(
        `${endDateOnline} ${endHourOnline}`,
        'DD/MM/YYYY HH:mm'
      );
      if (
        inicio.isBefore(fimOnline) &&
        [AssemblyModalities.HIBRIDO_PRESENCIAL_DIGITAL].includes(modo!)
      ) {
        return DEFAULT_VALIDATION_MODEL(
          true,
          'Deve ser posterior à hora de término presencial'
        );
      }

      if (
        initialDate &&
        validaData(String(initialDate)) &&
        endHour &&
        validaHora(String(endHour)) &&
        endDate &&
        validaData(String(endDate))
      ) {
        if (
          moment(initialDate, 'DD/MM/YYYY').isSame(
            moment(endDate, 'DD/MM/YYYY')
          )
        ) {
          const fim = moment(`${endDate} ${endHour}`, 'DD/MM/YYYY HH:mm');

          if (inicio.isAfter(fim)) {
            return DEFAULT_VALIDATION_MODEL(
              true,
              'Deve ser anterior à hora de término'
            );
          }
        }
      }
    }
    return DEFAULT_VALID;
  };

  validator.presentialEndHourIsValid = () => {
    const initialDate = presentialInitialDate;
    const endDate = presentialEndDate;
    const initialHour = presentialInitialHour;
    const endHour = presentialEndHour;
    const modo = modality;

    const required = requiredIsValid(endHour, 'Hora de término é obrigatória');

    if ([AssemblyModalities.HIBRIDO_PRESENCIAL_DIGITAL].includes(modo!)) {
      if (required.error) {
        return required;
      }
      const isValid = validaHora(String(endHour));
      if (!isValid) {
        return DEFAULT_VALIDATION_MODEL(true, 'Hora inválida');
      }
      if (
        initialDate &&
        validaData(String(initialDate)) &&
        initialHour &&
        validaHora(String(initialHour)) &&
        endDate &&
        validaData(String(endDate))
      ) {
        if (
          moment(initialDate, 'DD/MM/YYYY').isSame(
            moment(endDate, 'DD/MM/YYYY')
          )
        ) {
          const inicio = moment(
            `${initialDate} ${initialHour}`,
            'DD/MM/YYYY HH:mm'
          );
          const fim = moment(`${endDate} ${endHour}`, 'DD/MM/YYYY HH:mm');
          if (inicio.isAfter(fim)) {
            return DEFAULT_VALIDATION_MODEL(
              true,
              'Deve ser posterior à hora de início'
            );
          }
        }
      }
    }

    return DEFAULT_VALID;
  };

  validator.quantityAssociatesIsValid = () => {
    const value = quantityAssociates;
    const required = requiredIsValid(
      value,
      'Quantidade de associados é obrigatório'
    );
    const modo = modality;

    if (
      !isDateLessThanToday(
        new Date(
          [AssemblyModalities.HIBRIDO_PRESENCIAL_DIGITAL].includes(modo!)
            ? ISOToLocaleDateAndHour(
                presentialInitialDate!,
                presentialInitialHour!
              )
            : ISOToLocaleDateAndHour(digitalInitialDate, digitalInitialHour)
        )
      )
    ) {
      if (modo !== AssemblyModalities.OFFLINE) {
        if (quantityAssociates !== null && Number(quantityAssociates) === 0) {
          return DEFAULT_VALIDATION_MODEL(
            true,
            'Quantidade de associados não pode ser 0'
          );
        }

        if (required.error) {
          return required;
        }
      }
    }

    return DEFAULT_VALID;
  };

  validator.allIsValid = () => {
    return allIsFalse([
      validator?.digitalInitialDateIsValid!().error,
      validator?.digitalEndDateIsValid!().error,
      validator?.digitalInitialHourIsValid!().error,
      validator?.digitalEndHourIsValid!().error,
      validator?.presentialInitialDateIsValid!().error,
      validator?.presentialEndDateIsValid!().error,
      validator?.presentialInitialHourIsValid!().error,
      validator?.presentialEndHourIsValid!().error,
      validator?.quantityAssociatesIsValid!().error,
    ]);
  };

  return validator as CloneEventValidationType;
};
